import React, { useState, useEffect, useMemo, ChangeEvent } from 'react'
import styles from './SalesDetails.module.css'
import {
    TransactionResponse,
    getTransaccionesLocalPorFechasTipoPago,
    saleDateFormat,
} from '../../services/commerce'
import {
    endOfMonth,
    format,
    startOfMonth,
    subMonths,
    startOfWeek,
} from 'date-fns'
import SaleComponent from '../../components/saleComponent/SaleComponent'
import OutlinedSelect from '../../components/outlinedSelect/OutlinedSelect'
import { labels } from '../../labels'
import {
    addSales,
    currencyFormat,
    getFormatDate,
    getIncrease,
} from '../../formatters'
import Text from '../../components/text/Text'
import SvgIcon, { iconNames } from '../../components/svgIcon/SvgIcon'
import { getLocal } from '../../Utils/Profile'
import ModalComponent from '../../components/modal/ModalComponent'
import moment from 'moment'
import Calendar from '../../components/calendar/Calendar'
import 'react-datepicker/dist/react-datepicker.css'
import { useLoadingContext } from '../../context/spinnerContext'

const tokens: { [key: string]: string | string[] } = {
    [labels.allPayments]: '',
    [labels.debit]: ['BCIDEB', 'GETNETDEB'],
    [labels.credit]: ['BCICRE', 'GETNETCRE'],
    [labels.amiRut]: ['3487990009', '3487990079'],
    [labels.codWeb]: [
        '3487990016',
        '3487990017',
        '3487990018',
        '3487990019',
        '3487990020',
        '3487990021',
        '3487990022',
        '3487990023',
    ],
    [labels.codApp]: ['3487990007'],
    [labels.efectivo]: ['AMWEFEEFEEFE'],
    [labels.qr]: [
        '3487990005',
        '3487990008',
        '3487990010',
        '3487990011',
        '3487990011',
        '3487990012',
        '3487990013',
        '3487990024',
        '3487990025',
        '3487990100',
        '3487990101',
        '3487990102',
        '3487990103',
        '3487990104',
        '3487990105',
        '3487990106',
        '3487991502',
        '3487991503',
        '3487991504',
        '3487991505',
        '3487991506',
        '3487991511',
        '3487997011',
        '3487997101',
        '3487997502',
        '3487997503',
        '3487997505',
        '3487998011',
        '3487998102',
        '3487998502',
        '3487998503',
        '3487998505',
    ],
    [labels.efectivo]: ['AMSEFEEFEEFE', 'AMWEFEEFEEFE'],
    [labels.prepago]: [
        '2PW0CCTBKPRE',
        '2PU0CCTBKPRE',
        '2AP0CCTBKPRE',
        '2AU0CCTBKPRE',
    ],
    [labels.POSManual]: ['AMWTARPOSBAN', 'AMSTARPOSBAN'],
}

const payments = [
    { id: labels.allPayments, label: labels.allPayments },
    { id: labels.credit, label: labels.credit },
    { id: labels.debit, label: labels.debit },
    // { id: labels.amiRut, label: labels.amiRut },
    // { id: labels.codWeb, label: labels.codWeb },
    // { id: labels.codApp, label: labels.codApp },
    { id: labels.qr, label: labels.qr },
    { id: labels.efectivo, label: labels.efectivo },
    // { id: labels.prepago, label: labels.prepago },
    { id: labels.POSManual, label: labels.POSManual },
]

const datesOption = [
    { id: `D${format(new Date(), saleDateFormat)}`, label: labels.today },
    {
        id: `W${format(
            startOfWeek(new Date(), { weekStartsOn: 1 }),
            saleDateFormat
        )}`,
        label: labels.thisWeek,
    },
    {
        id: `M${format(startOfMonth(new Date()), saleDateFormat)}`,
        label: labels.thisMonth,
    },
    {
        id: `T${format(
            startOfMonth(subMonths(new Date(), 3)),
            saleDateFormat
        )}`,
        label: labels.threeMonthsAgo,
    },
    {
        id: `S${format(
            startOfMonth(subMonths(new Date(), 6)),
            saleDateFormat
        )}`,
        label: labels.sixMonthsAgo,
    },
    {
        id: labels.customDate,
        label: labels.customDate,
    },
]

export default function SalesDetails() {
    const currentUser = localStorage.getItem('currentUser')
    const loadingContext = useLoadingContext()

    const user = currentUser && JSON.parse(currentUser)
    const [data, setData] = useState<TransactionResponse[]>([])
    const [prevSales, setPrevSales] = useState<TransactionResponse[]>([])
    const [filterType, setFilterType] = useState<string>('')
    const [dateFrom, setDateFrom] = useState<string | undefined>(
        format(startOfMonth(new Date()), saleDateFormat)
    )
    const [filterLabel, setFilterLabel] = useState<string>(labels.thisMonth)
    const [openFilter, setOpenFilter] = useState(false)
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        null,
        null,
    ])
    const [showCalendarPicker, setShowCalendarPicker] = useState(false)

    const [startDate, endDate] = dateRange
    const [actualMonthSales, setActualMonthSales] = useState<
        TransactionResponse[] | undefined
    >(undefined)

    function removeFirstLetterIfNotNumber(text: string): string {
        if (text.length > 0 && isNaN(parseInt(text[0]))) {
            return text.slice(1)
        }
        return text
    }

    const handleFilterTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedOption = payments.find(
            (element) => element.label === event.target.value
        )
        setFilterType(selectedOption?.id || payments[0].id)
    }

    const handleDateFromChange = (event: { target: { value: string } }) => {
        const selectedOption = datesOption.find(
            (element) => element.label === event.target.value
        )

        if (selectedOption?.label === labels.customDate) {
            setOpenFilter(true)
            setFilterLabel(labels.customDate)
        } else {
            setDateRange([null, null])
            handlerCloseFilterPicker()
            setDateFrom(undefined)
            setTimeout(() => {
                setDateFrom(selectedOption?.id || datesOption[0].id)
                setFilterLabel(selectedOption?.label || labels.thisMonth)
            }, 100)
        }
    }

    const getSales = async (startDate: string, endDate: string) => {
        return await getTransaccionesLocalPorFechasTipoPago(
            user.sEstablecimiento,
            getLocal(),
            startDate,
            endDate
        )
    }
    const getPrevSales = async () => {
        const prevMonth = subMonths(new Date(), 1)
        const start = startOfMonth(prevMonth)
        const end = endOfMonth(start)
        await getSales(
            format(start, saleDateFormat),
            format(end, saleDateFormat)
        ).then((data) => {
            setPrevSales(data)
        })
    }

    const filterData = useMemo(() => {
        if (!tokens[filterType]) {
            return data
        } else {
            if (typeof tokens[filterType] !== 'string') {
                return data.filter((element) =>
                    tokens[filterType].includes(element.sToken)
                )
            } else {
                return data
            }
        }
    }, [data, filterType])

    const prevFilterData = useMemo(() => {
        if (!tokens[filterType]) {
            return prevSales
        } else {
            if (typeof tokens[filterType] !== 'string') {
                return prevSales.filter((element) =>
                    tokens[filterType].includes(element.sToken)
                )
            } else {
                return prevSales
            }
        }
    }, [prevSales, filterType])

    const actualSalesAmmount = useMemo(() => {
        return filterData.reduce(addSales, 0)
    }, [filterData])

    const prevSalesAmmount = useMemo(() => {
        return prevFilterData.reduce(addSales, 0)
    }, [prevFilterData])

    const actualTipsAmount = useMemo(() => {
        return filterData.reduce((total, sale) => {
            //suma propinas si la transacción no está anulada
            if (
                sale.sEstadoTx !== 'ANULADA' &&
                sale.sTipoTransaccion !== 'AN'
            ) {
                return total + (parseFloat(sale.sTip) || 0)
            }
            return total
        }, 0)
    }, [filterData])

    const incress = useMemo(() => {
        return getIncrease(prevSalesAmmount, actualSalesAmmount)
    }, [actualSalesAmmount, prevSales, filterType])

    const findMovements = () => {
        if (dateFrom) {
            const df = removeFirstLetterIfNotNumber(dateFrom)
            loadingContext?.onOpen()
            const from = startDate ? format(startDate, saleDateFormat) : df
            const to = endDate || new Date()
            getSales(from, format(to, saleDateFormat))
                .then((res) => {
                    setData(res.reverse())
                })
                .finally(() => {
                    loadingContext?.onClose()
                })
        }
    }
    useEffect(() => {
        if (dateFrom) {
            findMovements()
        }
    }, [dateFrom])

    useEffect(() => {
        getPrevSales()
    }, [])

    const handlerRefresh = () => {
        if (dateFrom) {
            const df = removeFirstLetterIfNotNumber(dateFrom)
            getSales(df, format(new Date(), saleDateFormat)).then((res) => {
                setData(res.reverse())
            })
        }
    }

    const handleModalFilter = () => {
        handlerCloseFilterPicker()
        findMovements()
    }

    const handleModalClearFilter = () => {
        handlerCloseFilterPicker()
        handleDateFromChange({
            target: {
                value: labels.thisMonth,
            },
        })
    }

    const filterPhrases: { [key: string]: string } = {
        [labels.today]: 'Hoy has vendido',
        [labels.thisWeek]: 'Esta semana has vendido',
        [labels.thisMonth]: 'Este mes has vendido',
        [labels.threeMonthsAgo]: 'Los últimos 3 meses has vendido',
        [labels.sixMonthsAgo]: 'Los últimos 6 meses has vendido',
        [labels.customDate]: 'Entre las fechas seleccionadas has vendido',
    }

    const formatDate = (date: Date | null) => {
        return date ? moment(date).format('DD/MM/YYYY') : 'Elige una fecha'
    }

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            setShowCalendarPicker(false)
        }
    }, [dateRange])

    const handlerCloseFilterPicker = () => {
        setOpenFilter(false)
        setShowCalendarPicker(false)
    }

    const filterModal = () => {
        return (
            <ModalComponent
                show={openFilter}
                handleClose={handleModalClearFilter}
                title={'Filtrar por fecha personalizada'}
                modalStyle={styles.filterModalStyle}
                titleMarginLeft={4}
                titleColor={'#565656'}
            >
                <Text
                    label="Selecciona las fechas para filtrar"
                    fontSize={13}
                    fontWeight={600}
                    fontFamily={'Nunito'}
                    color={'#565656'}
                    marginBottom={20}
                    marginLeft={5}
                    marginTop={20}
                />
                <div style={{ position: 'relative' }}>
                    <div className={styles.buttonRowContainer}>
                        <div
                            className={styles.outlinedButton}
                            onClick={() =>
                                setShowCalendarPicker(!showCalendarPicker)
                            }
                        >
                            <Text
                                label="Desde"
                                fontSize={14}
                                style={{
                                    position: 'absolute',
                                    top: -10,
                                    backgroundColor: 'white',
                                    paddingLeft: 4,
                                    paddingRight: 4,
                                    left: 20,
                                }}
                            />
                            <Text label={formatDate(startDate)} fontSize={14} />
                        </div>
                        <div
                            className={styles.outlinedButton}
                            onClick={() =>
                                setShowCalendarPicker(!showCalendarPicker)
                            }
                        >
                            <Text
                                label="Hasta"
                                fontSize={14}
                                style={{
                                    position: 'absolute',
                                    top: -10,
                                    backgroundColor: 'white',
                                    paddingLeft: 4,
                                    paddingRight: 4,
                                    left: 20,
                                }}
                            />
                            <Text label={formatDate(endDate)} fontSize={14} />
                        </div>
                    </div>
                    {showCalendarPicker && (
                        <div className={styles.calendarComponentSales}>
                            <Calendar
                                startDate={startDate}
                                endDate={endDate}
                                onChange={(update) => {
                                    setDateRange(
                                        update as unknown as [
                                            Date | null,
                                            Date | null
                                        ]
                                    )
                                }}
                                minDate={
                                    new Date(
                                        new Date().setMonth(
                                            new Date().getMonth() - 6
                                        )
                                    )
                                }
                                maxDate={new Date()}
                                locale="es"
                                selectsRange
                                inline
                            />
                        </div>
                    )}
                </div>
                <div className={styles.flexRow}>
                    <div
                        className={styles.buttonModalStyle}
                        onClick={handleModalClearFilter}
                    >
                        <Text
                            label="Borrar fecha"
                            fontSize={14}
                            fontWeight={800}
                            fontFamily={'Nunito'}
                        />
                    </div>
                    <div
                        className={
                            dateRange[0] && dateRange[1]
                                ? styles.buttonPrimaryModalStyle
                                : styles.buttonModalDisabledStyle
                        }
                        onClick={
                            dateRange[0] && dateRange[1]
                                ? handleModalFilter
                                : undefined
                        }
                    >
                        <Text
                            label="Aplicar fecha"
                            fontSize={14}
                            fontWeight={800}
                            fontFamily={'Nunito'}
                        />
                    </div>
                </div>
            </ModalComponent>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.headerTop}>
                        <Text
                            label={
                                filterLabel ? filterPhrases[filterLabel] : ''
                            }
                            fontWeight={600}
                            color="#565656"
                            fontSize={13}
                        />

                        <div className={styles.headerFilterContainer}>
                            <Text
                                label="Filtrar por: "
                                fontWeight={600}
                                marginRight={20}
                                color="#565656"
                                fontSize={13}
                            />
                            <div>
                                <OutlinedSelect
                                    options={payments}
                                    contentStyle={{
                                        margin: 0,
                                    }}
                                    inputStyle={{
                                        border: '1px solid #565656',
                                    }}
                                    selectStyle={{
                                        margin: '5px 10px 5px 10px',
                                    }}
                                    onChange={handleFilterTypeChange}
                                />
                            </div>
                            <div
                                style={{
                                    minWidth: 210,
                                    marginLeft: 15,
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <OutlinedSelect
                                    forceValue={
                                        dateRange[0] !== null &&
                                        dateRange[1] !== null
                                            ? `${getFormatDate(
                                                  dateRange[0]
                                              )} - ${getFormatDate(
                                                  dateRange[1]
                                              )}`
                                            : undefined
                                    }
                                    value={
                                        datesOption.find(
                                            (i) => i.label === filterLabel
                                        )?.id
                                    }
                                    options={datesOption}
                                    contentStyle={{
                                        margin: 0,
                                    }}
                                    inputStyle={{
                                        border: '1px solid #565656',
                                    }}
                                    selectStyle={{
                                        margin: '5px 10px 5px 10px',
                                    }}
                                    onChange={handleDateFromChange}
                                    defaultValue={datesOption[2].id}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.headerAmount}>
                        <Text
                            label={currencyFormat(actualSalesAmmount)}
                            fontWeight={600}
                            fontSize={48}
                            color={
                                !isNaN(Number(actualSalesAmmount)) &&
                                Number(actualSalesAmmount) < 0
                                    ? 'red'
                                    : '#184287'
                            }
                        />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <Text
                                    label={labels.totalTip}
                                    fontWeight={600}
                                    color="#2699D6"
                                    fontSize={10}
                                    fontFamily={'Poppins'}
                                />
                            </div>

                            <Text
                                label={currencyFormat(actualTipsAmount)}
                                fontWeight="600"
                                color="#ffff"
                                fontSize={20}
                                style={{
                                    backgroundColor: '#0086D6',
                                    borderRadius: 15,
                                    paddingInline: 20,
                                }}
                            />
                        </div>
                        <div className={styles.headerAmountPercentage}>
                            {filterLabel === labels.thisMonth && (
                                <>
                                    {prevSales.length > 0 ? (
                                        <>
                                            <div style={{ marginRight: 10 }}>
                                                <SvgIcon
                                                    iconName={
                                                        incress > 0
                                                            ? iconNames.ArrowUpGreen
                                                            : iconNames.arrowDown
                                                    }
                                                />
                                            </div>
                                            <Text
                                                label={`${
                                                    incress < 0
                                                        ? `0% de crecimiento respecto del mes anterior.`
                                                        : `${incress}${labels.incressPercentage}`
                                                }`}
                                                style={{
                                                    textTransform: 'uppercase',
                                                }}
                                                fontWeight={600}
                                                color="#184287"
                                                fontSize={16}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ marginRight: 10 }}>
                                                <SvgIcon
                                                    iconName={'IconTimeReload'}
                                                />
                                            </div>
                                            <Text
                                                label="No registra ventas el mes anterior."
                                                style={{
                                                    textTransform: 'uppercase',
                                                }}
                                                fontWeight={600}
                                                color="#184287"
                                                fontSize={16}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.body}>
                    {filterData && filterData.length > 0
                        ? filterData.map((element, indx) => {
                              return (
                                  <div
                                      key={`${element.idTransaccion}-${element.dTransaccion}-${element.nMonto}-${indx}`}
                                      className={styles.bodyItem}
                                  >
                                      <SaleComponent
                                          sale={element}
                                          handlerRefresh={handlerRefresh}
                                      />
                                  </div>
                              )
                          })
                        : null}
                </div>
            </div>
            {filterModal()}
        </div>
    )
}
